import { useState, useRef, useEffect, useCallback } from 'react';

// WebSocket connection states (string literals)
export const WebSocketConnectionStates = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
};

const CHAT_UNREAD_COUNT_KEY = 'chat_unread_count';
const getLastReadStorageKey = (sessionId) => `chat_last_read_at_${sessionId}`;

const getStoredLastReadAt = (sessionId) => {
  if (!sessionId) return null;
  return localStorage.getItem(getLastReadStorageKey(sessionId));
};

const setStoredLastReadAt = (sessionId, timestamp) => {
  if (!sessionId) return;
  localStorage.setItem(getLastReadStorageKey(sessionId), timestamp);
};

const getStoredUnreadCount = () => {
  const storedUnread = Number(localStorage.getItem(CHAT_UNREAD_COUNT_KEY) || 0);
  return Number.isFinite(storedUnread) && storedUnread > 0 ? storedUnread : 0;
};

/**
 * Simple WebSocket service (simplified version)
 * Note: This is a basic implementation. You may need to enhance it based on your WebSocket server requirements.
 */
class SimpleChatWebSocketService {
  constructor() {
    this.ws = null;
    this.sessionId = null;
    this.handlers = {};
    this.connectionState = WebSocketConnectionStates.DISCONNECTED;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  getConnectionState() {
    return this.connectionState;
  }

  async connect(sessionId, handlers) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.sessionId === sessionId) {
      console.log('🔌 Already connected to session:', sessionId);
      return;
    }

    this.sessionId = sessionId;
    this.handlers = handlers;
    this.connectionState = WebSocketConnectionStates.CONNECTING;

    try {
      const wsUrl = process.env.REACT_APP_WEBSOCKET_URL + `api/v1/sessions/${sessionId}/ws` ;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('🔌 WebSocket connected');
        this.connectionState = WebSocketConnectionStates.CONNECTED;
        this.reconnectAttempts = 0;
        this.handlers.onConnectionStateChange?.(this.connectionState);
      };

      this.ws.onmessage = (event) => {
        try {
          console.log('🔌 WebSocket raw message received:', event.data);
          const data = JSON.parse(event.data);
          console.log('🔌 WebSocket parsed data:', data);
          
          // Handle direct message format: { from: 'agent', message: '...', timestamp: '...', ... }
          // No 'type' key - messages come directly
          if (data.from || data.from_user) {
            const fromValue = data.from || data.from_user;
            
            // Filter only agent and customer messages
            if (fromValue === 'agent' || fromValue === 'customer') {
              console.log('🔌 Passing message to handler:', data);
              this.handlers.onMessage?.(data);
            } else {
              console.log('🔌 Filtered out message from:', fromValue);
            }
          } else {
            console.log('🔌 Message without from/from_user key:', data);
          }
        } catch (error) {
          console.error('🔌 Error parsing WebSocket message:', error);
          console.error('🔌 Raw message data:', event.data);
        }
      };

      this.ws.onerror = (error) => {
        console.error('🔌 WebSocket error:', error);
        this.connectionState = WebSocketConnectionStates.ERROR;
        this.handlers.onConnectionStateChange?.(this.connectionState);
        this.handlers.onError?.({ data: { error: 'WebSocket connection error' } });
      };

      this.ws.onclose = () => {
        console.log('🔌 WebSocket closed');
        this.connectionState = WebSocketConnectionStates.DISCONNECTED;
        this.handlers.onConnectionStateChange?.(this.connectionState);
        
        // Attempt to reconnect
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          setTimeout(() => {
            this.connect(this.sessionId, this.handlers);
          }, 3000 * this.reconnectAttempts);
        }
      };
    } catch (error) {
      console.error('🔌 Failed to connect WebSocket:', error);
      this.connectionState = WebSocketConnectionStates.ERROR;
      this.handlers.onConnectionStateChange?.(this.connectionState);
      throw error;
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.sessionId = null;
    this.connectionState = WebSocketConnectionStates.DISCONNECTED;
  }

  sendTyping(isTyping) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'typing',
        isTyping: isTyping,
      }));
    }
  }

  async testConnection(sessionId) {
    // Simple test - just check if we can create a WebSocket
    try {
      const wsUrl = process.env.REACT_APP_WEBSOCKET_URL + `api/v1/sessions/${sessionId}/ws`;
      const testWs = new WebSocket(wsUrl);
      return new Promise((resolve) => {
        testWs.onopen = () => {
          testWs.close();
          resolve({ success: true });
        };
        testWs.onerror = () => {
          resolve({ success: false, error: 'WebSocket connection failed' });
        };
        setTimeout(() => {
          testWs.close();
          resolve({ success: false, error: 'WebSocket connection timeout' });
        }, 5000);
      });
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export const chatWebSocketService = new SimpleChatWebSocketService();

/**
 * Custom hook for managing WebSocket connection in chat support
 * Simplified version without context dependency
 */
export const useChatWebSocket = (options = {}) => {
  const [connectionState, setConnectionState] = useState(WebSocketConnectionStates.DISCONNECTED);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(getStoredUnreadCount);
  const [hasNewMessages, setHasNewMessages] = useState(getStoredUnreadCount() > 0);
  const [isTyping, setIsTyping] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [sessionCleared, setSessionCleared] = useState(false);
  const sessionIdRef = useRef(null);

  // Update connection state
  const handleConnectionStateChange = useCallback((state) => {
    setConnectionState(state);
    setIsConnected(state === WebSocketConnectionStates.CONNECTED);
    options.onConnectionStateChange?.(state);
  }, [options]);

  // Handle incoming messages
  const handleMessage = useCallback((wsMessage) => {
    console.log('🔌 handleMessage called with:', wsMessage);
    
    // Convert WebSocket message format to ChatMessage format
    // WebSocket format: { from: 'agent', message: '...', timestamp: '...', agent_id: '...', agent_name: '...' }
    // ChatMessage needs: { id, sessionId, message, sender, timestamp, isRead }
    
    // Map 'from' to 'sender'
    let sender = 'user';
    const fromValue = wsMessage.from || wsMessage.from_user;
    
    if (fromValue === 'agent') {
      sender = 'support';
    } else if (fromValue === 'customer') {
      sender = 'user';
    }
    
    // Generate message ID if not provided
    const messageId = wsMessage.message_id || wsMessage.id || 
      `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const chatMessage = {
      id: messageId,
      sessionId: wsMessage.session_id || wsMessage.sessionId || sessionIdRef.current,
      message: wsMessage.message || wsMessage.text || '',
      sender: sender,
      timestamp: wsMessage.timestamp || new Date().toISOString(),
      isRead: wsMessage.isRead !== undefined ? wsMessage.isRead : false
    };
    
    console.log('🔌 Converted ChatMessage:', chatMessage);
    
    // Check for duplicates before adding
    setMessages(prev => {
      const exists = prev.some(msg => 
        msg.id === chatMessage.id || 
        (msg.message === chatMessage.message && 
         msg.sender === chatMessage.sender && 
         msg.sessionId === chatMessage.sessionId &&
         Math.abs(new Date(msg.timestamp).getTime() - new Date(chatMessage.timestamp).getTime()) < 2000)
      );
      
      if (exists) {
        console.log('🔌 Duplicate message detected, skipping');
        return prev;
      }
      
      console.log('🔌 Adding new message. Total messages:', prev.length + 1);
      // Sort messages chronologically after adding
      const updatedMessages = [...prev, chatMessage].sort((a, b) => {
        const timeA = new Date(a.timestamp).getTime();
        const timeB = new Date(b.timestamp).getTime();
        return timeA - timeB;
      });
      return updatedMessages;
    });
    
    // Update unread count if chat is not open
    if (!isChatOpen) {
      setUnreadCount(prev => {
        const nextUnreadCount = prev + 1;
        localStorage.setItem(CHAT_UNREAD_COUNT_KEY, String(nextUnreadCount));
        return nextUnreadCount;
      });
      setHasNewMessages(true);
    }
    
    options.onMessage?.(chatMessage);
  }, [options, isChatOpen]);

  // Handle typing events
  const handleTyping = useCallback((event) => {
    console.log('🔌 Typing event received:', event.data);
    setIsTyping(event.data?.isTyping || false);
    options.onTyping?.(event.data?.isTyping || false);
  }, [options]);

  // Handle status events
  const handleStatus = useCallback((event) => {
    console.log('🔌 Status event received:', event.data);
    options.onStatus?.(event);
  }, [options]);

  // Handle error events
  const handleError = useCallback((event) => {
    console.error('🔌 WebSocket error received:', event.data);
    options.onError?.(event.data?.error || 'WebSocket error');
  }, [options]);

  // Connect to WebSocket
  const connect = useCallback(async (sessionId) => {
    if (sessionIdRef.current === sessionId && isConnected) {
      console.log('🔌 Already connected to session:', sessionId);
      return;
    }

    sessionIdRef.current = sessionId;
    
    const handlers = {
      onMessage: handleMessage,
      onTyping: handleTyping,
      onStatus: handleStatus,
      onError: handleError,
      onConnectionStateChange: handleConnectionStateChange
    };

    try {
      await chatWebSocketService.connect(sessionId, handlers);
      console.log('🔌 WebSocket connected successfully for session:', sessionId);
    } catch (error) {
      console.error('🔌 Failed to connect WebSocket:', error);
      options.onError?.(error instanceof Error ? error.message : 'Failed to connect');
    }
  }, [handleMessage, handleTyping, handleStatus, handleError, handleConnectionStateChange, options, isConnected]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    console.log('🔌 Disconnecting WebSocket');
    chatWebSocketService.disconnect();
    sessionIdRef.current = null;
    setIsConnected(false);
    setConnectionState(WebSocketConnectionStates.DISCONNECTED);
  }, []);

  // Send typing indicator
  const sendTyping = useCallback((typing) => {
    if (!isConnected) {
      return;
    }
    
    chatWebSocketService.sendTyping(typing);
  }, [isConnected]);

  // Add message
  const addMessage = useCallback((message) => {
    setMessages(prev => {
      // Check for duplicates
      const exists = prev.some(msg => 
        msg.id === message.id 
      );
      if (exists) {
        return prev;
      }
      return [...prev, message];
    });
  }, []);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Mark messages as read
  const markMessagesAsRead = useCallback((sessionId) => {
    const resolvedSessionId = sessionId || sessionIdRef.current || localStorage.getItem('chat_session_id');
    setUnreadCount(0);
    setHasNewMessages(false);
    localStorage.setItem(CHAT_UNREAD_COUNT_KEY, '0');
    setStoredLastReadAt(resolvedSessionId, new Date().toISOString());
    setMessages(prev => prev.map(msg => ({ ...msg, isRead: true })));
  }, []);

  // Reset notification state
  const resetNotificationState = useCallback((sessionId) => {
    const resolvedSessionId = sessionId || sessionIdRef.current || localStorage.getItem('chat_session_id');
    setUnreadCount(0);
    setHasNewMessages(false);
    localStorage.setItem(CHAT_UNREAD_COUNT_KEY, '0');
    setStoredLastReadAt(resolvedSessionId, new Date().toISOString());
  }, []);

  // Recalculate unread count from fetched history when app resumes/reloads
  const syncUnreadFromHistory = useCallback((sessionId, historyMessages = []) => {
    if (!sessionId || !Array.isArray(historyMessages)) {
      return;
    }

    if (isChatOpen) {
      markMessagesAsRead(sessionId);
      return;
    }

    const lastReadAt = getStoredLastReadAt(sessionId);
    const lastReadTime = lastReadAt ? new Date(lastReadAt).getTime() : 0;

    const unreadFromHistory = historyMessages.filter(message => {
      if (message.sender !== 'support') {
        return false;
      }

      const messageTime = new Date(message.timestamp).getTime();
      if (Number.isNaN(messageTime)) {
        return false;
      }

      return messageTime > lastReadTime;
    }).length;

    setUnreadCount(unreadFromHistory);
    setHasNewMessages(unreadFromHistory > 0);
    localStorage.setItem(CHAT_UNREAD_COUNT_KEY, String(unreadFromHistory));
  }, [isChatOpen, markMessagesAsRead]);

  // Set chat open state
  const setChatOpen = useCallback((open, onClose) => {
    setIsChatOpen(open);
    if (open) {
      markMessagesAsRead();
    }
  }, [markMessagesAsRead]);

  // Reset session cleared flag
  const resetSessionClearedFlag = useCallback(() => {
    setSessionCleared(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('🔌 Cleaning up WebSocket connection');
      disconnect();
    };
  }, [disconnect]);

  // Update connection state from service
  useEffect(() => {
    const currentState = chatWebSocketService.getConnectionState();
    setConnectionState(currentState);
    setIsConnected(currentState === WebSocketConnectionStates.CONNECTED);
  }, []);

  return {
    connectionState,
    isConnected,
    messages,
    unreadCount,
    hasNewMessages,
    isTyping,
    sessionCleared,
    connect,
    disconnect,
    sendTyping,
    addMessage,
    clearMessages,
    markMessagesAsRead,
    resetNotificationState,
    syncUnreadFromHistory,
    setChatOpen,
    resetSessionClearedFlag,
  };
};
